import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ulid } from 'ulid';
import { useLive, useRuntime } from './context';
import { canChangeCatalog } from './seat';
import { putWithProgress } from './upload';
import { useT } from '../messages';
import { ItemPhotoUpload, ItemPhotos as ItemPhotosAnswer } from '../wire';
import type { ItemImage } from '../wire';

/**
 * **Photographs of what the business sells** (B7, `docs/console-redesign-plan.md`
 * §3 — the owner, 2026-09-26: "add pictures when defining products, in the
 * admin or the PWA").
 *
 * One control for both editors — a product on the shelf panel, a treatment on
 * the menu — because it is one table underneath (`item_media`) and one set of
 * routes mounted twice (`core-api`'s `routes/item-photos.ts`).
 *
 * ## Whoever may change the catalog, and the screen says so rather than drawing a dead button
 *
 * `core-api` answers `403` on these routes to every seat the business's
 * "who may change what we sell" setting leaves out (B10 — the owner by
 * default, the owner and staff when she says so; `readonly` never). So the
 * controls are drawn for `canChangeCatalog` only. Every other seat — and the
 * console's support look — sees the pictures, because they are the shelf and
 * the desk should know what the jar looks like; the panel around this control
 * says in one sentence why there is nothing to press.
 *
 * ## On the website, per photograph (B10)
 *
 * Each picture carries its own switch. Off keeps it on the item — here, in its
 * place, with its description — and off the business's website; every seat
 * sees the marker, so nobody wonders why the page shows two pictures of three.
 * The switch goes through the same arrange `PUT` as a reorder, naming only the
 * photograph that changed, because the server keeps what every other one said.
 *
 * ## Online-only, said plainly, and disabled when the device knows it is offline
 *
 * `Albums.tsx`'s boundary and its reason: there is nowhere to queue a
 * photograph. The outbox holds JSON intents; a picture is bytes, PUT straight
 * to the bucket against a URL that expires within the hour. So the sentence is
 * always there, and the picker is **disabled while `navigator.onLine` is
 * false** — the browser's own word about the network, which is not
 * `useConnection()` (the event stream's state, which `Albums.tsx` rightly
 * refuses to gate on). When the browser says online and the network is not, the
 * request fails and the row prints the server's — or the browser's — sentence.
 *
 * ## Alt text is asked for before anything moves
 *
 * Every chosen picture gets its box, and Upload stays greyed until each has a
 * description. The one moment the owner is certain to be looking at the picture
 * is this one; asking later is asking never. The server refuses a blank one
 * too, so this is the kind version of a rule rather than the rule.
 *
 * ## `accept="image/*"`, and nothing past `<input type="file">`
 *
 * On an iPad that opens the camera and the photo library both, and Safari hands
 * back a JPEG for a library photo stored as HEIC. A HEIC that does get through
 * is refused by the server with a sentence saying what to do, which the row
 * shows verbatim.
 */

/** `ITEM_MEDIA_MAX` in `packages/store` — the database's own bound, restated for the picker. */
const MAX_PHOTOS = 12;

type RowState = 'ready' | 'uploading' | 'done' | 'error';

interface Pending {
  key: string;
  file: File;
  preview: string | null;
  alt: string;
  state: RowState;
  percent: number | null;
  error: string | null;
}

export type PhotoMount = 'products' | 'services';

export function ItemPhotos({
  mount,
  itemId,
  itemName,
  images,
}: {
  mount: PhotoMount;
  itemId: string;
  itemName: string;
  /** What the mirror holds for this item; `undefined` when it has not said. */
  images: readonly ItemImage[] | undefined;
}): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const session = useLive(() => runtime.mirror.session(), null);
  const online = useOnline();
  const inputRef = useRef<HTMLInputElement | null>(null);

  /**
   * The list the last answer from the server said, until the mirror catches up.
   * The attach and arrange routes answer with the item's list as it now stands,
   * so this is the server's word, not an optimistic guess.
   */
  const [answered, setAnswered] = useState<ItemImage[] | null>(null);
  const [pending, setPending] = useState<Pending[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  // A new mirror row supersedes the answer it was refreshed from.
  useEffect(() => {
    setAnswered(null);
  }, [images]);

  // Previews are object URLs, which hold the file in memory until revoked. Rows
  // revoke their own as they leave; this is the editor closing with some left.
  const pendingRef = useRef<Pending[]>([]);
  pendingRef.current = pending;
  useEffect(
    () => () => {
      for (const row of pendingRef.current) revoke(row.preview);
    },
    [],
  );

  const shown = answered ?? images ?? [];
  const canEdit = canChangeCatalog(session);
  const base = `/${mount}/${itemId}/photos`;

  const refreshMirror = useCallback(async (): Promise<void> => {
    try {
      if (mount === 'products') await runtime.mirror.refreshProducts();
      else await runtime.mirror.refreshCatalog();
    } catch {
      // The answer on screen is already the server's; the next cue or hydrate
      // brings the mirror along.
    }
  }, [runtime, mount]);

  const choose = (files: FileList | null): void => {
    if (files === null) return;
    const room = MAX_PHOTOS - shown.length - pending.length;
    const chosen = [...files].slice(0, Math.max(0, room));
    setFailure(null);
    setPending((current) => [
      ...current,
      ...chosen.map((file) => ({
        key: ulid(),
        file,
        preview: previewOf(file),
        alt: '',
        state: 'ready' as const,
        percent: null,
        error: null,
      })),
    ]);
    if (inputRef.current !== null) inputRef.current.value = '';
  };

  const mark = (key: string, patch: Partial<Pending>): void => {
    setPending((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const discard = (key: string): void => {
    setPending((current) => {
      revoke(current.find((row) => row.key === key)?.preview ?? null);
      return current.filter((row) => row.key !== key);
    });
  };

  /**
   * Plan, PUT, attach — one picture at a time, `Albums.tsx`'s reason: on a shop
   * wifi, parallel uploads slow together and the progress stops meaning
   * anything. Nothing is recorded until the bytes have landed.
   */
  const upload = async (): Promise<void> => {
    const rows = pending.filter((row) => row.state === 'ready' || row.state === 'error');
    if (rows.length === 0) return;
    setBusy(true);
    setFailure(null);
    for (const row of rows) {
      mark(row.key, { state: 'uploading', percent: 0, error: null });
      try {
        const contentType = row.file.type === '' ? 'image/jpeg' : row.file.type;
        const planned = await runtime.api.request({
          method: 'POST',
          path: `${base}/upload-url`,
          body: { content_type: contentType, bytes: row.file.size },
          schema: ItemPhotoUpload,
        });
        await putWithProgress(planned.url, row.file, planned.content_type, (percent) => {
          mark(row.key, { percent });
        });
        const attached = await runtime.api.request({
          method: 'POST',
          path: base,
          body: {
            upload_id: planned.upload_id,
            content_type: planned.content_type,
            alt: row.alt.trim(),
          },
          idempotencyKey: ulid(),
          schema: ItemPhotosAnswer,
        });
        setAnswered(attached.images);
        revoke(row.preview);
        setPending((current) => current.filter((r) => r.key !== row.key));
      } catch (e) {
        // One picture's failure is one picture's: the row keeps its file and
        // its description, says why, and Upload tries it again.
        mark(row.key, {
          state: 'error',
          percent: null,
          error: e instanceof Error ? e.message : String(e),
        });
      }
    }
    setBusy(false);
    await refreshMirror();
  };

  /**
   * `flip` names the one photograph whose website switch changes; every other
   * entry is its id alone, and the server keeps what each of them said.
   */
  const arrange = async (
    next: readonly ItemImage[],
    flip?: { mediaId: string; onWebsite: boolean },
  ): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      const answer = await runtime.api.request({
        method: 'PUT',
        path: base,
        body: {
          photos: next.map((image) =>
            flip !== undefined && image.media_id === flip.mediaId
              ? { media_id: image.media_id, on_website: flip.onWebsite }
              : { media_id: image.media_id },
          ),
        },
        idempotencyKey: ulid(),
        schema: ItemPhotosAnswer,
      });
      setAnswered(answer.images);
      await refreshMirror();
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  const move = (index: number, by: -1 | 1): void => {
    const next = [...shown];
    const [picked] = next.splice(index, 1);
    if (picked === undefined) return;
    next.splice(index + by, 0, picked);
    void arrange(next);
  };

  /* ---- Everybody the catalog is not theirs to change: the pictures, and no doors. ---- */
  if (!canEdit) {
    if (shown.length === 0) return null;
    return (
      <div className="item-photos" aria-label={t('photos.heading')}>
        <ul className="item-photos__list">
          {shown.map((image) => (
            <li key={image.media_id} className="item-photos__item">
              <img src={image.url} alt={image.alt} loading="lazy" width={72} height={72} />
              <WebsiteMark onWebsite={image.on_website} />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const full = shown.length + pending.length >= MAX_PHOTOS;
  const missingAlt = pending.some((row) => row.alt.trim() === '');
  const waiting = pending.filter((row) => row.state === 'ready' || row.state === 'error').length;

  return (
    <div className="item-photos">
      <h3 className="item-photos__heading">{t('photos.heading')}</h3>
      <p className="muted">{t('photos.needsConnection')}</p>
      {online ? null : (
        <p className="note" role="status">
          {t('photos.offline')}
        </p>
      )}
      {failure === null ? null : (
        <p className="error" role="alert">
          {failure}
        </p>
      )}

      {shown.length === 0 ? (
        <p className="muted">{t('photos.none')}</p>
      ) : (
        <ul className="item-photos__list">
          {shown.map((image, index) => (
            <li key={image.media_id} className="item-photos__item">
              <img src={image.url} alt={image.alt} loading="lazy" width={72} height={72} />
              <WebsiteMark onWebsite={image.on_website} />
              <div className="item-photos__actions">
                <label className="checkbox item-photos__site">
                  <input
                    type="checkbox"
                    checked={image.on_website}
                    aria-label={t('photos.onWebsiteFor', { alt: image.alt })}
                    disabled={!online || busy}
                    onChange={(e) =>
                      void arrange(shown, {
                        mediaId: image.media_id,
                        onWebsite: e.target.checked,
                      })
                    }
                  />
                  <span>{t('photos.onWebsite')}</span>
                </label>
                <button
                  type="button"
                  className="link-button"
                  aria-label={t('photos.moveEarlier', { alt: image.alt })}
                  disabled={!online || busy || index === 0}
                  onClick={() => move(index, -1)}
                >
                  ←
                </button>
                <button
                  type="button"
                  className="link-button"
                  aria-label={t('photos.moveLater', { alt: image.alt })}
                  disabled={!online || busy || index === shown.length - 1}
                  onClick={() => move(index, 1)}
                >
                  →
                </button>
                {confirming === image.media_id ? (
                  <span className="item-photos__confirm">
                    <span>{t('photos.removeConfirm')}</span>
                    <button
                      type="button"
                      className="link-button link-button--danger"
                      disabled={!online || busy}
                      onClick={() =>
                        void arrange(shown.filter((i) => i.media_id !== image.media_id))
                      }
                    >
                      {t('photos.removeYes')}
                    </button>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setConfirming(null)}
                    >
                      {t('photos.keep')}
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="link-button link-button--danger"
                    aria-label={t('photos.remove', { alt: image.alt })}
                    disabled={!online || busy}
                    onClick={() => setConfirming(image.media_id)}
                  >
                    {t('common.remove')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {pending.length === 0 ? null : (
        <ul className="item-photos__pending">
          {pending.map((row) => (
            <li key={row.key} className={`upload upload--${row.state}`}>
              {row.preview === null ? null : (
                <img src={row.preview} alt="" width={56} height={56} />
              )}
              <label className="item-photos__alt">
                <span>{t('photos.altLabel')}</span>
                <input
                  value={row.alt}
                  maxLength={300}
                  required
                  disabled={row.state === 'uploading'}
                  placeholder={t('photos.altPlaceholder', { name: itemName })}
                  onChange={(e) => mark(row.key, { alt: e.target.value })}
                />
              </label>
              <span className="upload__state">
                {row.state === 'uploading'
                  ? row.percent === null
                    ? t('photos.waiting')
                    : t('photos.percent', { percent: row.percent })
                  : row.state === 'error'
                    ? (row.error ?? t('photos.failed'))
                    : null}
              </span>
              {row.state === 'uploading' ? null : (
                <button type="button" className="link-button" onClick={() => discard(row.key)}>
                  {t('photos.discard')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {pending.length > 0 && missingAlt ? <p className="note">{t('photos.altRequired')}</p> : null}

      <div className="row-actions">
        {full ? (
          <p className="note">{t('photos.full', { max: MAX_PHOTOS })}</p>
        ) : (
          <label className="button button--ghost item-photos__pick">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              disabled={!online || busy}
              aria-label={t('photos.choose')}
              onChange={(e) => choose(e.target.files)}
            />
            <span>{t('photos.choose')}</span>
          </label>
        )}
        {waiting === 0 ? null : (
          <button
            type="button"
            className="button"
            disabled={!online || busy || missingAlt}
            onClick={() => void upload()}
          >
            {t('photos.upload', { count: waiting })}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The words under a photograph a stranger does not see (B10). Nothing under
 * one they do: the marker is for the exception, and twelve "On the website"
 * labels would bury the one that is not.
 */
function WebsiteMark({ onWebsite }: { onWebsite: boolean }): ReactNode {
  const t = useT();
  if (onWebsite) return null;
  return <small className="item-photos__off">{t('photos.offWebsite')}</small>;
}

/**
 * The first photograph, small, for a list row or a till chip — decorative
 * (`alt=""`) because the item's name is printed right beside it, and a screen
 * reader saying the name twice helps nobody.
 */
export function ItemThumb({
  images,
  className = 'item-thumb',
}: {
  images: readonly ItemImage[] | undefined;
  className?: string;
}): ReactNode {
  const first = images?.[0];
  if (first === undefined) return null;
  return <img className={className} src={first.url} alt="" loading="lazy" width={40} height={40} />;
}

/**
 * The browser's own word about the network, kept current.
 *
 * Not `useConnection()`: that is the event stream, and a stream that blipped on
 * a working network must not take the picker away (`Albums.tsx`). This only
 * ever says "offline" when the browser is sure — no interface at all — which is
 * exactly when an upload cannot possibly work.
 */
function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  );
  useEffect(() => {
    const on = (): void => setOnline(true);
    const off = (): void => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

function previewOf(file: File): string | null {
  return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null;
}

function revoke(preview: string | null): void {
  if (preview !== null && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(preview);
}
